import { useState, useEffect, useCallback, memo, type ReactElement } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  MiscellaneousServices as MiscellaneousServicesIcon,
  VpnKey as VpnKeyIcon,
  Folder as FolderIcon,
  Widgets as WidgetsIcon,
} from '@mui/icons-material';
import ModalHeader from '../../../shared/components/ModalHeader';
import ModalFooter from '../../../shared/components/ModalFooter';
import ServiceComponentsTabContent from './ServiceComponentsTabContent';
import ServiceAdminTabContent from './ServiceAdminTabContent';
import PipelineAccessTabContent from './PipelineAccessTabContent';
import PortfolioManagementPanel from './PortfolioManagementPanel';

interface CatalogManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Two-context spine: OFFERINGS (what we sell — browse-by-category + editor of one
// ServiceOffering set) | PROVISIONING (how it's materialized). The Components tab
// groups the provisioning-side surfaces as sub-sections.
type PanelId = 'offering' | 'component' | 'portfolio' | 'pipeline';

const COMPONENT_SUBTABS: { id: PanelId; label: string; icon: ReactElement }[] = [
  { id: 'component', label: 'Components', icon: <WidgetsIcon fontSize="small" /> },
  { id: 'portfolio', label: 'Portfolios', icon: <FolderIcon fontSize="small" /> },
  { id: 'pipeline', label: 'Pipeline Access', icon: <VpnKeyIcon fontSize="small" /> },
];

const SINGULAR: Record<PanelId, string> = {
  offering: 'offering', component: 'component', portfolio: 'portfolio', pipeline: 'pipeline credential',
};

const CatalogManagementModal = memo<CatalogManagementModalProps>(({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState(0); // 0 = Offerings, 1 = Components
  const [subTab, setSubTab] = useState(0);       // sub-section within Components
  const [counts, setCounts] = useState<Record<PanelId, number>>({
    offering: 0, component: 0, portfolio: 0, pipeline: 0,
  });

  useEffect(() => {
    if (isOpen) { setActiveTab(0); setSubTab(0); }
  }, [isOpen]);

  const makeCountHandler = useCallback((id: PanelId) => (count: number) => {
    setCounts(prev => prev[id] === count ? prev : { ...prev, [id]: count });
  }, []);

  const activePanel: PanelId = activeTab === 0 ? 'offering' : COMPONENT_SUBTABS[subTab].id;
  const footerCount = counts[activePanel] || 0;
  const footerLabel = `${SINGULAR[activePanel]}${footerCount !== 1 ? 's' : ''}`;

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth={false} PaperProps={{ sx: { width: '95vw', height: '95vh', maxWidth: '95vw', maxHeight: '95vh' } }}>
      <ModalHeader icon={InventoryIcon} title="Service Catalog" onClose={onClose} desktopRecommended />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Offerings" icon={<MiscellaneousServicesIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
          <Tab label="Components" icon={<WidgetsIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
        </Tabs>
      </Box>

      {activeTab === 1 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, bgcolor: 'action.hover' }}>
          <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} variant="scrollable" scrollButtons="auto">
            {COMPONENT_SUBTABS.map((t) => (
              <Tab key={t.id} label={t.label} icon={t.icon} iconPosition="start" sx={{ minHeight: 40 }} />
            ))}
          </Tabs>
        </Box>
      )}

      <DialogContent sx={{ p: 0, display: 'flex', height: '100%', overflow: 'hidden' }}>
        {activeTab === 0 && <ServiceAdminTabContent isOpen={isOpen && activeTab === 0} onCountChange={makeCountHandler('offering')} />}
        {activeTab === 1 && subTab === 0 && <ServiceComponentsTabContent isOpen={isOpen && activeTab === 1 && subTab === 0} onCountChange={makeCountHandler('component')} />}
        {activeTab === 1 && subTab === 1 && <PortfolioManagementPanel isOpen={isOpen && activeTab === 1 && subTab === 1} onCountChange={makeCountHandler('portfolio')} />}
        {activeTab === 1 && subTab === 2 && <PipelineAccessTabContent isOpen={isOpen && activeTab === 1 && subTab === 2} onCountChange={makeCountHandler('pipeline')} />}
      </DialogContent>

      <ModalFooter count={footerCount} label={footerLabel} onClose={onClose} />
    </Dialog>
  );
});

CatalogManagementModal.displayName = 'CatalogManagementModal';

export default CatalogManagementModal;
